import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell } from "lucide-react";

export function ago(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
}

export function useNotifications(limit = 30) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["notifications", limit],
    queryFn: async () => (await api.get("/notifications", { params: { limit } })).data,
    refetchInterval: 30000,
  });
  const read = useMutation({
    mutationFn: async (body) => (await api.post("/notifications/read", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  return { ...query, read };
}

// The bell in the sidebar: unread count, the latest few, and the way to all of them and email settings.
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data, read } = useNotifications(10);
  const unread = data?.unread || 0;
  const items = data?.notifications || [];
  // the warning colour while a scheduled update that ran on credit or is held waits unread (design screen 10)
  const money = items.some((n) => !n.read_at && ["document_on_credit", "document_held"].includes(n.kind));

  const go = (n) => {
    if (!n.read_at) read.mutate({ ids: [n.id] });
    setOpen(false);
    navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="flex-1 px-0 relative" data-testid="notifications-bell"
          aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"} title="Notifications">
          <Bell />
          {unread > 0 && (
            <span className={cn("absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full text-xs font-semibold tabular-nums leading-5",
              money ? "bg-gr-warning text-background" : "bg-primary text-primary-foreground")}
              data-testid="notifications-unread">{unread > 9 ? "9+" : unread}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" className="w-80 p-0" data-testid="notifications-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-display font-semibold tracking-[0.02em]">Notifications</span>
          {unread > 0 && (
            <button className="text-sm text-muted-foreground hover:text-foreground" onClick={() => read.mutate({ all: true })}>
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground px-4 py-6 text-center">Nothing yet.</p>
          ) : items.map((n) => (
            // as on the Notifications page: unread is a filled dot and a semibold title; rows take the hover wash
            <button key={n.id} onClick={() => go(n)}
              className="w-full text-left px-4 py-3 border-b last:border-0 transition-colors duration-300 ease-out-expo hover:bg-gr-hover">
              <div className="flex items-start gap-3">
                <span className={cn("mt-2 w-2 h-2 rounded-full shrink-0", n.read_at ? "bg-transparent" : "bg-primary")} aria-hidden="true" />
                <div className="min-w-0">
                  <div className={cn("text-sm", !n.read_at && "font-semibold")}>
                    {!n.read_at && <span className="sr-only">Unread: </span>}{n.title}</div>
                  <div className="text-sm text-gr-ink-secondary line-clamp-2 mt-0.5">{n.body}</div>
                  <div className="text-xs text-muted-foreground mt-1">{ago(n.created_at)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
        <Link to="/notifications" onClick={() => setOpen(false)} className="block text-sm font-semibold text-center py-3 border-t hover:opacity-80">
          All notifications and email settings
        </Link>
      </PopoverContent>
    </Popover>
  );
}
